import {
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InfoIcon from '@mui/icons-material/Info';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';

import ISNCDashboard, {
    ISNCDashboardStatus,
    ISNCDashboardTeam,
} from '../../../Models/SNCDashboard/SNCDashboard.model';
import { SNCDashboardCompareDateRange } from '../../../Enum/SNCDashboardCompareDateRange';
import { SNCDashboardMetricDateRange } from '../../../Enum/SNCDashboardMetricDateRange';
import { setShowMessage } from '../../../store/shared.slice';
import { useAppDispatch } from '../../../store/store';
import { ERROR } from '../../../utils/constants';
import { getSNCDashboard } from './SNCDashboardService';
import { useNavigate } from 'react-router-dom';
import { SNCDashboardDrillDownType } from '../../../Enum/SNCDashboardDrillDownType';
import { useWidgetContext } from '../../../hooks/widgetContext';

const colors = {
    blue: '#1976d2',
    lightBlue: '#e3f2fd',
    green: '#2e7d32',
    lightGreen: '#e8f5e9',
    orange: '#ed6c02',
    lightOrange: '#fff3e0',
    purple: '#7b1fa2',
    lightPurple: '#f3e5f5',
    red: '#d32f2f',
    text: '#1f2937',
    secondary: '#64748b',
    border: '#d9dde3',
    background: '#ffffff',
    tableHeader: '#fafafa',
};

const cardSx = {
    border: `1px solid ${colors.border}`,
    borderRadius: 1,
    boxShadow: 'none',
    bgcolor: colors.background,
};

const requestTabContainerHeight = 350;

const requestTableColumnWidths = {
    first: '40%',
    today: '30%',
    compare: '30%',
};

const requestTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    '& .MuiTableCell-root': {
        px: 1.5,
        py: 0.8,
    },
};

const metricDateRanges = [
    { label: SNCDashboardMetricDateRange.TodayDescription, value: SNCDashboardMetricDateRange.Today },
    { label: SNCDashboardMetricDateRange.YesterdayDescription, value: SNCDashboardMetricDateRange.Yesterday },
    { label: SNCDashboardMetricDateRange.Last7DaysDescription, value: SNCDashboardMetricDateRange.Last7Days },
    { label: SNCDashboardMetricDateRange.Last30DaysDescription, value: SNCDashboardMetricDateRange.Last30Days },
];

const formatPoints = (value: number) =>
    Number.isInteger(value)
        ? value.toString()
        : value.toFixed(1);

const formatDateQuery = (value: Date | null) => {
    if (!value)
        return undefined;

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value: Date | null) => {
    if (!value)
        return '';

    return value.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const showErrorMessage = (error: any, fallback: string) => {
    if (typeof error?.response?.data === 'string')
        return error.response.data;

    if (error?.response?.data?.error)
        return error.response.data.error;

    if (error?.response?.data?.message)
        return error.response.data.message;

    return fallback;
};

const MetricDateSelect = ({
    value,
    onChange,
}: {
    value: SNCDashboardMetricDateRange;
    onChange: (value: SNCDashboardMetricDateRange) => void;
}) => (
    <FormControl size="small" sx={{ minWidth: 110 }}>
        <Select
            value={value}
            onChange={(event) =>
                onChange(
                    Number(event.target.value) as SNCDashboardMetricDateRange
                )
            }
            sx={{
                height: 32,
                minWidth: 110,
                fontSize: 12,
                '& .MuiSelect-select': {
                    py: 0.4,
                    px: 1,
                    pr: '32px !important',
                },
            }}
        >
            {metricDateRanges.map((range) => (
                <MenuItem
                    key={range.value}
                    value={range.value}
                >
                    {range.label}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

const DailyPointsGoal = ({
    dashboard,
    onClick,
}: {
    dashboard: ISNCDashboard;
    onClick?: () => void;
}) => {
    const updateItem =
        dashboard.dailyPointBreakdown.find(
            (item) =>
                item.requestType
                    ?.toLowerCase() ===
                'updating request/notes'
        );

    const completedToday =
        dashboard.dailyPointBreakdown
            .filter(
                (item) =>
                    item.requestType
                        ?.toLowerCase() !==
                    'updating request/notes'
            )
            .reduce(
                (total, item) =>
                    total +
                    item.completedToday,
                0
            );

    const updatedToday =
        updateItem?.completedToday ??
        0;

    const progress =
        Math.min(
            100,
            dashboard.dailyProgressPercentage
        );

    return (
        <Card
            onClick={onClick}
            sx={{
                ...cardSx,
                height: '100%',
                cursor:
                    onClick
                        ? 'pointer'
                        : 'default',

                '&:hover':
                    onClick
                        ? {
                            boxShadow:
                                '0 2px 8px rgba(0,0,0,0.12)',
                            borderColor:
                                '#b8c6d9',
                        }
                        : undefined,
            }}
        >
            <CardContent
                sx={{
                    p: 1.15,
                    '&:last-child': {
                        pb: 1.15,
                    },
                }}
            >
                <Stack
                    spacing={0.75}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                bgcolor:
                                    colors.lightOrange,
                                color:
                                    colors.orange,
                                display:
                                    'grid',
                                placeItems:
                                    'center',
                            }}
                        >
                            <EmojiEventsOutlinedIcon
                                sx={{
                                    fontSize: 20,
                                }}
                            />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color:
                                    colors.secondary,
                            }}
                        >
                            Daily Points Goal
                        </Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.6}
                        alignItems="baseline"
                    >
                        <Typography
                            sx={{
                                fontSize: 25,
                                lineHeight: 1,
                                fontWeight: 700,
                                color:
                                    colors.text,
                            }}
                        >
                            {formatPoints(
                                dashboard.pointsEarned
                            )}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 13,
                                color:
                                    colors.secondary,
                            }}
                        >
                            /{' '}
                            {formatPoints(
                                dashboard.dailyGoal
                            )}{' '}
                            pts today
                        </Typography>
                    </Stack>

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 6,
                            borderRadius: 10,
                            bgcolor:
                                '#e7edf4',
                            '& .MuiLinearProgress-bar':
                                {
                                    borderRadius: 10,
                                    bgcolor:
                                        colors.blue,
                                },
                        }}
                    />

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                color:
                                    colors.secondary,
                            }}
                        >
                            {completedToday}{' '}
                            completed ·{' '}
                            {updatedToday}{' '}
                            updates
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color:
                                    colors.blue,
                            }}
                        >
                            {formatPoints(
                                dashboard.dailyProgressPercentage
                            )}
                            %
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

const DailyPointsGoalSkeleton = () => (
    <Card sx={{ ...cardSx, height: '100%' }}>
        <CardContent
            sx={{
                p: 1.15,
                '&:last-child': { pb: 1.15 },
            }}
        >
            <Stack spacing={0.75}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                >
                    <Skeleton
                        variant="rounded"
                        width={34}
                        height={34}
                    />

                    <Skeleton
                        variant="text"
                        width={120}
                        height={20}
                    />
                </Stack>

                <Stack
                    direction="row"
                    spacing={0.6}
                    alignItems="baseline"
                >
                    <Skeleton
                        variant="text"
                        width={36}
                        height={25}
                    />

                    <Skeleton
                        variant="text"
                        width={90}
                        height={18}
                    />
                </Stack>

                <Skeleton
                    variant="rounded"
                    width="100%"
                    height={6}
                />

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Skeleton
                        variant="text"
                        width={145}
                        height={18}
                    />

                    <Skeleton
                        variant="text"
                        width={36}
                        height={18}
                    />
                </Stack>
            </Stack>
        </CardContent>
    </Card>
);

const RequestsPerTeam = ({
    teams,
    compareDate,
    onTeamClick,
}: {
    teams: ISNCDashboardTeam[];
    compareDate: Date | null;
    onTeamClick: (team: ISNCDashboardTeam) => void;
}) => {
    const currentTotal = teams.reduce(
        (total, team) => total + team.currentPeriodCount,
        0
    );

    const compareTotal = teams.reduce(
        (total, team) => total + team.comparePeriodCount,
        0
    );

    return (
        <Paper
            variant="outlined"
            sx={{
                height: '100%',
                width: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'none',
                borderColor: colors.border,
                overflow: 'hidden',
            }}
        >
            {/* STATIC HEADER */}
            <Table
                size="small"
                sx={requestTableSx}
            >
                <TableHead>
                    <TableRow
                        sx={{
                            bgcolor: colors.tableHeader,
                        }}
                    >
                        <TableCell
                            sx={{
                                width: requestTableColumnWidths.first,
                                fontWeight: 700,
                            }}
                        >
                            Team
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: requestTableColumnWidths.today,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 13,
                                }}
                            >
                                Today
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: colors.secondary,
                                }}
                            >
                                {formatDisplayDate(new Date())}
                            </Typography>
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: requestTableColumnWidths.compare,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 13,
                                }}
                            >
                                Comparison Date
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: colors.secondary,
                                }}
                            >
                                {formatDisplayDate(compareDate)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
            </Table>

            {/* SCROLL ONLY TEAM ROWS */}
            <TableContainer
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                <Table
                    size="small"
                    sx={requestTableSx}
                >
                    <TableBody>
                        {teams.map((team) => (
                            <TableRow
                                key={
                                    team.teamId ||
                                    team.teamName
                                }
                                hover
                                onClick={() =>
                                    onTeamClick(
                                        team
                                    )
                                }
                                sx={{
                                    cursor: 'pointer',

                                    '&:hover': {
                                        bgcolor:
                                            '#f5f9ff !important',
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        width: requestTableColumnWidths.first,
                                    }}
                                >
                                    {team.teamName}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: requestTableColumnWidths.today,
                                    }}
                                >
                                    {team.currentPeriodCount}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: requestTableColumnWidths.compare,
                                    }}
                                >
                                    {team.comparePeriodCount}
                                </TableCell>
                            </TableRow>
                        ))}

                        {!teams.length && (
                            <TableRow>
                                <TableCell
                                    colSpan={3}
                                    align="center"
                                    sx={{
                                        color: colors.secondary,
                                    }}
                                >
                                    No team request data available.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* STATIC TOTAL */}
            <Table
                size="small"
                sx={requestTableSx}
            >
                <TableBody>
                    <TableRow
                        sx={{
                            bgcolor: colors.tableHeader,
                            borderTop: `1px solid ${colors.border}`,
                        }}
                    >
                        <TableCell
                            sx={{
                                width: requestTableColumnWidths.first,
                                fontWeight: 700,
                            }}
                        >
                            Total
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: requestTableColumnWidths.today,
                                fontWeight: 700,
                            }}
                        >
                            {currentTotal}
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: requestTableColumnWidths.compare,
                                fontWeight: 700,
                            }}
                        >
                            {compareTotal}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Paper>
    );
};

const RequestsPerStatus = ({
    statuses,
    compareDate,
    onStatusClick,
}: {
    statuses: ISNCDashboardStatus[];
    compareDate: Date | null;
    onStatusClick: (status: string) => void;
}) => {
    const statusData = useMemo(() => {
        const getStatus = (name: string) => {
            const status = statuses.find(
                (item) =>
                    item.status
                        ?.toLowerCase()
                        .replace('-', ' ')
                        .trim() ===
                    name.toLowerCase()
            );

            return {
                status: name,
                currentPeriodCount:
                    status?.currentPeriodCount ?? 0,
                comparePeriodCount:
                    status?.comparePeriodCount ?? 0,
            };
        };

        return [
            {
                ...getStatus('New'),
                color: '#0288d1',
                background: '#e1f5fe',
            },
            {
                ...getStatus('In Progress'),
                color: '#ef6c00',
                background: '#fff3e0',
            },
            {
                ...getStatus('Completed'),
                color: '#2e7d32',
                background: '#e8f5e9',
            },
        ];
    }, [statuses]);

    const currentTotal = statusData.reduce(
        (total, item) =>
            total + item.currentPeriodCount,
        0
    );

    const compareTotal = statusData.reduce(
        (total, item) =>
            total + item.comparePeriodCount,
        0
    );

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                minWidth: 0,
            }}
        >
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    height: '100%',
                    width: '100%',
                    boxShadow: 'none',
                    borderColor: colors.border,
                    overflow: 'hidden',
                }}
            >
                <Table
                    size="small"
                    sx={requestTableSx}
                >
                    <TableHead>
                        <TableRow
                            sx={{
                                bgcolor: colors.tableHeader,
                            }}
                        >
                            <TableCell
                                sx={{
                                    width: requestTableColumnWidths.first,
                                    fontWeight: 700,
                                }}
                            >
                                Status
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: requestTableColumnWidths.today,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 13,
                                    }}
                                >
                                    Today
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: colors.secondary,
                                    }}
                                >
                                    {formatDisplayDate(
                                        new Date()
                                    )}
                                </Typography>
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: requestTableColumnWidths.compare,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 13,
                                    }}
                                >
                                    Comparison Date
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: colors.secondary,
                                    }}
                                >
                                    {formatDisplayDate(
                                        compareDate
                                    )}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {statusData.map((status) => (
                            <TableRow
                                key={
                                    status.status
                                }
                                hover
                                onClick={() =>
                                    onStatusClick(
                                        status.status
                                    )
                                }
                                sx={{
                                    cursor: 'pointer',

                                    '&:hover': {
                                        bgcolor:
                                            '#f5f9ff !important',
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        width: requestTableColumnWidths.first,
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        label={
                                            status.status
                                        }
                                        sx={{
                                            height: 22,
                                            bgcolor: status.background,
                                            color: status.color,
                                            fontWeight: 600,
                                        }}
                                    />
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: requestTableColumnWidths.today,
                                    }}
                                >
                                    {
                                        status.currentPeriodCount
                                    }
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: requestTableColumnWidths.compare,
                                    }}
                                >
                                    {
                                        status.comparePeriodCount
                                    }
                                </TableCell>
                            </TableRow>
                        ))}

                        <TableRow
                            sx={{
                                bgcolor: colors.tableHeader,
                            }}
                        >
                            <TableCell
                                sx={{
                                    width: requestTableColumnWidths.first,
                                    fontWeight: 700,
                                }}
                            >
                                Total
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: requestTableColumnWidths.today,
                                    fontWeight: 700,
                                }}
                            >
                                {currentTotal}
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: requestTableColumnWidths.compare,
                                    fontWeight: 700,
                                }}
                            >
                                {compareTotal}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const TableSkeleton = ({
    rows,
}: {
    rows: number;
}) => (
    <Box
        sx={{
            height: '100%',
            width: '100%',
            minWidth: 0,
        }}
    >
        <Paper
            variant="outlined"
            sx={{
                height: '100%',
                width: '100%',
                boxShadow: 'none',
                borderColor: colors.border,
                overflow: 'hidden',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    p: 1.5,
                    bgcolor: colors.tableHeader,
                }}
            >
                <Skeleton
                    variant="text"
                    width="28%"
                    height={24}
                />

                <Skeleton
                    variant="text"
                    width="20%"
                    height={24}
                />

                <Skeleton
                    variant="text"
                    width="28%"
                    height={24}
                />
            </Stack>

            {Array.from({
                length: rows,
            }).map((_, index) => (
                <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 1.5,
                        py: 0.8,
                        borderTop: `1px solid ${colors.border}`,
                    }}
                >
                    <Skeleton
                        variant="text"
                        width="30%"
                        height={22}
                    />

                    <Skeleton
                        variant="text"
                        width="12%"
                        height={22}
                    />

                    <Skeleton
                        variant="text"
                        width="12%"
                        height={22}
                    />
                </Stack>
            ))}
        </Paper>
    </Box>
);

const ActivityCard = ({
    title,
    value,
    dateRange,
    setDateRange,
    icon,
    color,
    background,
    onClick,
}: {
    title: string;
    value: number;
    dateRange: SNCDashboardMetricDateRange;
    setDateRange: (value: SNCDashboardMetricDateRange) => void;
    icon: React.ReactNode;
    color: string;
    background: string;
    onClick?: () => void;
}) => (
    <Card
        onClick={onClick}
        sx={{
            ...cardSx,
            height: '100%',
            cursor:
                onClick
                    ? 'pointer'
                    : 'default',

            '&:hover':
                onClick
                    ? {
                        boxShadow:
                            '0 2px 8px rgba(0,0,0,0.12)',
                    }
                    : undefined,
        }}
    >
        <CardContent
            sx={{
                p: 1.25,
                height: '100%',
                '&:last-child': {
                    pb: 1.25,
                },
            }}
        >
            <Stack
                spacing={1}
                height="100%"
            >
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            minWidth: 34,
                            borderRadius: 1.5,
                            bgcolor: background,
                            color,
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        {icon}
                    </Box>

                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: colors.secondary,
                            lineHeight: 1.25,
                        }}
                    >
                        {title}
                    </Typography>
                </Stack>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    spacing={1}
                    sx={{
                        mt: 'auto',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 28,
                            lineHeight: 1,
                            fontWeight: 700,
                            color,
                        }}
                    >
                        {value}
                    </Typography>

                    <Box
                        onClick={(
                            event
                        ) =>
                            event
                                .stopPropagation()
                        }
                    >
                        <MetricDateSelect
                            value={
                                dateRange
                            }
                            onChange={
                                setDateRange
                            }
                        />
                    </Box>
                </Stack>
            </Stack>
        </CardContent>
    </Card>
);

const ActivityCardSkeleton = () => (
    <Card sx={{ ...cardSx, height: '100%' }}>
        <CardContent
            sx={{
                p: 1.25,
                height: '100%',
                '&:last-child': { pb: 1.25 },
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="rounded" width={34} height={34} />
                    <Skeleton variant="text" width={115} height={24} />
                </Stack>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    spacing={1}
                >
                    <Skeleton variant="text" width={45} height={38} />
                    <Skeleton variant="rounded" width={110} height={32} />
                </Stack>
            </Stack>
        </CardContent>
    </Card>
);

const SNCDashboard = (props: any) => {
    const dispatch = useAppDispatch();

    const [showInfo, setShowInfo] =
        useState(false);

    const [isLoading, setIsLoading] =
        useState(true);

    const [dashboard, setDashboard] =
        useState<ISNCDashboard | null>(null);

    const {
        sncTeamId: teamId,
        setSncTeamId: setTeamId,
        sncProviderId: providerId,
        setSncProviderId: setProviderId,
        sncProviderOptions,
        setSncProviderOptions,
        sncCompareDate: compareDate,
        setSncCompareDate: setCompareDate,
        sncCompletedDateRange: completedDateRange,
        setSncCompletedDateRange: setCompletedDateRange,
        sncUpdatedDateRange: updatedDateRange,
        setSncUpdatedDateRange: setUpdatedDateRange,
        sncTeamOptions: teamOptions,
        setSncTeamOptions: setTeamOptions,
        setSncDrillDownType,
        setSncMetricDateRange,
        setSncStatus,
    } = useWidgetContext();

    const [requestsTab, setRequestsTab] =
        useState(0);

    const navigate =
        useNavigate();

    const openDrillDown =
        (
            drillDownType:
                SNCDashboardDrillDownType,
            options?: {
                teamId?: string;
                status?: string;
                metricDateRange?: SNCDashboardMetricDateRange;
            }
        ) => {
            setSncDrillDownType(drillDownType);
            setSncMetricDateRange(options?.metricDateRange ?? SNCDashboardMetricDateRange.Today);
            setSncStatus(drillDownType === SNCDashboardDrillDownType.Status ? options?.status : undefined);
            setTeamId(options?.teamId ?? teamId);
            navigate('/provider/metricsummary/sncdashboarddetail');
        };

    document.title =
        props.title ||
        'CHP - SNC Dashboard';

    const loadDashboard =
        useCallback(async () => {
            if (!compareDate)
                return;

            setIsLoading(true);

            try {
                const response =
                    await getSNCDashboard({
                        compareDateRange:
                            SNCDashboardCompareDateRange.Custom,

                        completedDateRange,

                        updatedDateRange,

                        compareDateFrom:
                            formatDateQuery(
                                compareDate
                            ),

                        compareDateTo:
                            formatDateQuery(
                                compareDate
                            ),

                        teamId:
                            !teamId ||
                            teamId === 'All'
                                ? undefined
                                : teamId,

                        providerId:
                            !providerId ||
                            providerId === 'All'
                                ? undefined
                                : providerId,
                    });

                setDashboard(response);

                if (response.requestsPerTeam?.length) {
                    setTeamOptions(
                        response.requestsPerTeam
                    );
                }

                if (response.providers?.length) {
                    setSncProviderOptions(
                        response.providers
                    );
                }
            }
            catch (error) {
                dispatch(
                    setShowMessage(
                        true,
                        showErrorMessage(
                            error,
                            'Unable to load SNC Dashboard.'
                        ),
                        ERROR as AlertColor
                    )
                );
            }
            finally {
                setIsLoading(false);
            }
        }, [
            compareDate,
            completedDateRange,
            updatedDateRange,
            teamId,
            providerId,
            dispatch,
            setTeamOptions,
            setSncProviderOptions,
        ]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const data: ISNCDashboard =
        dashboard ?? {
            totalRequests: 0,
            compareTotalRequests: 0,
            totalRequestsPercentageChange: 0,

            completedRequests: 0,
            compareCompletedRequests: 0,
            completedPercentageChange: 0,

            updatedRequests: 0,
            compareUpdatedRequests: 0,
            updatedPercentageChange: 0,

            dailyGoal: 0,
            pointsEarned: 0,
            dailyProgressPercentage: 0,
            pointsRemaining: 0,

            requestsPerTeam: [],
            requestsPerStatus: [],
            dailyPointBreakdown: [],
            providers: [],
        };

    const providerOptions = useMemo(
        () => (sncProviderOptions ?? [])
            .filter((provider) => Boolean(provider.providerId))
            .filter((provider, index, array) =>
                array.findIndex((item) => item.providerId === provider.providerId) === index
            )
            .sort((first, second) =>
                (first.providerName ?? '').localeCompare(second.providerName ?? '')
            ),
        [sncProviderOptions]
    );

    const requestTeamRows = useMemo(
        () => {
            if (!teamId || teamId === 'All')
                return data.requestsPerTeam;

            return data.requestsPerTeam.filter(
                (team) => team.teamId === teamId
            );
        },
        [data.requestsPerTeam, teamId]
    );

    return (
        <Grid size={{ xs: 6 }} display="flex">
            <Paper
                className="smartdashboard-widget"
                elevation={5}
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                }}
            >
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                >
                    <Box
                        sx={{
                            width: '100%',
                            p: 2,
                        }}
                    >
                        <Stack spacing={1.5}>
                            {/* HEADER */}

                            <Stack spacing={1.25}>
                                <Stack
                                    direction="row"
                                    justifyContent="center"
                                    alignItems="center"
                                    spacing={0.5}
                                >
                                    <Typography
                                        variant="h6"
                                        color="primary"
                                        sx={{ textAlign: 'center' }}
                                    >
                                        SNC Dashboard
                                    </Typography>

                                    <Tooltip
                                        open={showInfo}
                                        title={
                                            <Box sx={{ p: 0.75, maxWidth: 360 }}>
                                                <Stack spacing={0.8}>
                                                    <Typography variant="caption">
                                                        <strong>Requests Per Team:</strong>{' '}
                                                        Shows the number of open requests by team for today and the selected comparison date.
                                                    </Typography>

                                                    <Typography variant="caption">
                                                        <strong>Requests Per Status:</strong>{' '}
                                                        Shows requests by New, In Progress, and Completed status for today and the selected comparison date.
                                                    </Typography>

                                                    <Typography variant="caption">
                                                        <strong>Requests Completed:</strong>{' '}
                                                        Shows completed requests based on the selected date range, such as Today, Yesterday, Last 7 Days, or Last 30 Days.
                                                    </Typography>

                                                    <Typography variant="caption">
                                                        <strong>Requests Updated:</strong>{' '}
                                                        Shows requests where notes or updates were added during the selected date range.
                                                    </Typography>

                                                    <Typography variant="caption">
                                                        <strong>Comparison Date:</strong>{' '}
                                                        Compares Team and Status request counts with the selected historical date.
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        }
                                        placement="bottom"
                                        arrow
                                        PopperProps={{
                                            style: {
                                                zIndex: 1000,
                                            },
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowInfo(!showInfo)}
                                        >
                                            <InfoIcon
                                                sx={{
                                                    width: 18,
                                                    height: 18,
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="flex-start"
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    spacing={1.25}
                                    pt={1}
                                >
                                    <DesktopDatePicker
                                        label="Comparison Date"
                                        value={compareDate}
                                        minDate={new Date('2017-01-01')}
                                        maxDate={new Date()}
                                        onChange={setCompareDate}
                                        disabled={isLoading}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: {
                                                    width: 190,
                                                    '& .MuiInputBase-root': {
                                                        height: 40,
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                    },
                                                },
                                            },
                                        }}
                                    />

                                    {isLoading && teamOptions.length === 0 ? (
                                        <Skeleton
                                            variant="rounded"
                                            width={230}
                                            height={40}
                                        />
                                    ) : (
                                        <FormControl
                                            size="small"
                                            disabled={isLoading}
                                            sx={{ width: 230 }}
                                        >
                                            <InputLabel>
                                                Team
                                            </InputLabel>

                                            <Select
                                                value={teamId || 'All'}
                                                label="Team"
                                                onChange={(event) => setTeamId(event.target.value)}
                                                sx={{ height: 40 }}
                                            >
                                                <MenuItem value="All">All</MenuItem>

                                                {teamOptions.map((team) => (
                                                    <MenuItem
                                                        key={team.teamId || team.teamName}
                                                        value={team.teamId}
                                                    >
                                                        {team.teamName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <FormControl
                                        size="small"
                                        disabled={isLoading}
                                        sx={{ width: 230 }}
                                    >
                                        <InputLabel>
                                            SNC Provider
                                        </InputLabel>

                                        <Select
                                            value={providerId || 'All'}
                                            label="SNC Provider"
                                            onChange={(event) => setProviderId(event.target.value)}
                                            sx={{ height: 40 }}
                                        >
                                            <MenuItem value="All">All</MenuItem>

                                            {providerOptions.map((provider) => (
                                                <MenuItem
                                                    key={provider.providerId}
                                                    value={provider.providerId}
                                                >
                                                    {provider.providerName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Stack>

                            {/* DAILY POINTS / COMPLETED / UPDATED */}

                            <Grid container spacing={1.5} alignItems="stretch" pt={2}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    {isLoading ? (
                                        <DailyPointsGoalSkeleton />
                                    ) : (
                                        <DailyPointsGoal
                                            dashboard={data}
                                            onClick={() =>
                                                openDrillDown(
                                                    SNCDashboardDrillDownType.DailyPoints
                                                )
                                            }
                                        />
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    {isLoading ? (
                                        <ActivityCardSkeleton />
                                    ) : (
                                        <ActivityCard
                                            title="Requests Completed"
                                            value={data.completedRequests}
                                            dateRange={completedDateRange}
                                            setDateRange={setCompletedDateRange}
                                            icon={
                                                <CheckCircleOutlineIcon
                                                    sx={{ fontSize: 20 }}
                                                />
                                            }
                                            color={colors.green}
                                            background={colors.lightGreen}
                                            onClick={() =>
                                                openDrillDown(
                                                    SNCDashboardDrillDownType.Completed,
                                                    {
                                                        metricDateRange:
                                                            completedDateRange,
                                                    }
                                                )
                                            }
                                        />
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    {isLoading ? (
                                        <ActivityCardSkeleton />
                                    ) : (
                                        <ActivityCard
                                            title="Requests Updated"
                                            value={data.updatedRequests}
                                            dateRange={updatedDateRange}
                                            setDateRange={setUpdatedDateRange}
                                            icon={
                                                <UpdateOutlinedIcon
                                                    sx={{ fontSize: 20 }}
                                                />
                                            }
                                            color={colors.blue}
                                            background={colors.lightBlue}
                                            onClick={() =>
                                                openDrillDown(
                                                    SNCDashboardDrillDownType.Updated,
                                                    {
                                                        metricDateRange:
                                                            updatedDateRange,
                                                    }
                                                )
                                            }
                                        />
                                    )}
                                </Grid>
                            </Grid>

                            {/* TOTAL REQUESTS / REQUEST TABS */}

                            <Grid container spacing={1.5} alignItems="stretch">

                                <Grid size={{ xs: 12, md: 12 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            ...cardSx,
                                            width: '100%',
                                            minWidth: 0,
                                            height: requestTabContainerHeight,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Tabs
                                            value={requestsTab}
                                            onChange={(_, value) => setRequestsTab(value)}
                                            variant="fullWidth"
                                            sx={{
                                                minHeight: 42,
                                                borderBottom: `1px solid ${colors.border}`,
                                                '& .MuiTab-root': {
                                                    minHeight: 42,
                                                    fontWeight: 700,
                                                    textTransform: 'none',
                                                },
                                            }}
                                        >
                                            <Tab
                                                icon={
                                                    <GroupsOutlinedIcon
                                                        sx={{ fontSize: 20 }}
                                                    />
                                                }
                                                iconPosition="start"
                                                label="Requests Per Team"
                                            />

                                            <Tab
                                                icon={
                                                    <AssignmentTurnedInOutlinedIcon
                                                        sx={{ fontSize: 20 }}
                                                    />
                                                }
                                                iconPosition="start"
                                                label="Requests Per Status"
                                            />
                                        </Tabs>

                                        <Box
                                            sx={{
                                                p: 1.5,
                                                flex: 1,
                                                minHeight: 0,
                                                width: '100%',
                                                minWidth: 0,
                                                boxSizing: 'border-box',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            {isLoading ? (
                                                <TableSkeleton rows={requestsTab === 0 ? 6 : 4} />
                                            ) : requestsTab === 0 ? (
                                                <RequestsPerTeam
                                                    teams={
                                                        requestTeamRows
                                                    }
                                                    compareDate={
                                                        compareDate
                                                    }
                                                    onTeamClick={(
                                                        team
                                                    ) =>
                                                        openDrillDown(
                                                            SNCDashboardDrillDownType.Team,
                                                            {
                                                                teamId:
                                                                    team.teamId,
                                                            }
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <RequestsPerStatus
                                                    statuses={
                                                        data.requestsPerStatus
                                                    }
                                                    compareDate={
                                                        compareDate
                                                    }
                                                    onStatusClick={(
                                                        status
                                                    ) =>
                                                        openDrillDown(
                                                            SNCDashboardDrillDownType.Status,
                                                            {
                                                                status,
                                                            }
                                                        )
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Box>
                </LocalizationProvider>
            </Paper>
        </Grid>
    );
};

export default SNCDashboard;
